import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Users, Activity, UserPlus, Video, Crown, Loader2 } from "lucide-react";

const POLL_MS = 8_000; // gentle poll — the endpoint hits Mongo counts on each call

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.max(0, n || 0));

function StatTile({ icon: Icon, label, value, sub, accent, live, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative border border-white/10 bg-[#0A0A0C] p-4 hover:border-white/20 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1 flex items-center gap-1.5">
            {label}
            {live && (
              <span className="relative inline-flex h-1.5 w-1.5">
                <span
                  className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
                  style={{ background: accent }}
                />
                <span
                  className="relative inline-flex rounded-full h-1.5 w-1.5"
                  style={{ background: accent }}
                />
              </span>
            )}
          </p>
          <p
            className="font-display font-black text-3xl text-white leading-none tabular-nums"
            style={{ textShadow: `0 0 12px ${accent}55` }}
          >
            {value}
          </p>
          {sub && <p className="font-mono text-[10px] text-white/40 mt-1.5">{sub}</p>}
        </div>
        <Icon className="h-5 w-5 shrink-0" style={{ color: accent }} strokeWidth={1.8} />
      </div>
    </div>
  );
}

export default function LiveStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/stats/live");
      setStats(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load live stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    // pause polling when tab hidden so we don't hammer prod on background tabs
    const onVis = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        load();
        timerRef.current = setInterval(load, POLL_MS);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center gap-2 text-white/40 text-xs font-mono py-4">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        // loading live pulse…
      </div>
    );
  }

  if (err) {
    return (
      <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 text-xs font-mono text-[#FF3B30]" data-testid="live-stats-err">
        {err}
      </div>
    );
  }

  return (
    <section data-testid="live-stats" aria-label="Live platform stats">
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
          // live.pulse
        </p>
        <p className="font-mono text-[9px] uppercase tracking-widest text-white/25">
          auto-refresh · {POLL_MS / 1000}s
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          testid="stat-online-now"
          icon={Activity}
          label="online.now"
          value={fmt(stats.online_now)}
          sub="active websocket sessions"
          accent="#39FF14"
          live
        />
        <StatTile
          testid="stat-total-users"
          icon={Users}
          label="total.users"
          value={fmt(stats.total_users)}
          sub={`${fmt(stats.pro_users)} on Pro`}
          accent="#B026FF"
        />
        <StatTile
          testid="stat-signups-24h"
          icon={UserPlus}
          label="signups.24h"
          value={`+${fmt(stats.new_users_24h)}`}
          sub={`+${fmt(stats.new_users_7d)} in 7d`}
          accent="#00E5FF"
        />
        <StatTile
          testid="stat-videos"
          icon={Video}
          label="videos.total"
          value={fmt(stats.total_videos)}
          sub={`+${fmt(stats.videos_24h)} in 24h`}
          accent="#FF007F"
        />
      </div>
    </section>
  );
}
